import type { DisbursementEvent, Loan, LoanEvent, RateChangeEvent, RepaymentConsequence } from '../../types';
import { computeSchedule, countGenuineCycles, remainingInstalmentsToMaturity, type ComputedSchedule } from './engine';
import { calculateEmi } from './emi';

/**
 * ============================================================================
 * SAVINGS BASELINES — Baseline A / B / C, computed via the existing engine
 * ============================================================================
 *
 * This module contains NO independent calculation logic of its own beyond
 * simple arithmetic differences (interest saved, payment saved, time
 * saved). Every baseline is computed by calling the SAME
 * computeSchedule() engine with a deliberately constructed synthetic
 * event list — exactly the same technique already used successfully by
 * computeRepaymentBasis(). There is no second calculation system here.
 *
 * All three baselines respect the loan's ACTUAL staged disbursement
 * timeline — every baseline includes the real DisbursementEvent history
 * unchanged, per the explicit product requirement that a staged-
 * disbursement loan's baselines must never assume a hypothetical
 * lump-sum or different disbursement schedule.
 *
 * BASELINE A — Original Loan:
 *   Actual disbursements only. No rate changes, no prepayments, no
 *   consequences of any kind. The loan runs at loan.originalAnnualRatePercent
 *   for its entire life, with EMI = loan.originalEmiAtFullDisbursement
 *   throughout (the engine's own default resolution when no rate-change/
 *   prepayment/correction events exist at all — nothing extra needs to be
 *   constructed here beyond the disbursement events themselves).
 *
 * BASELINE B — Rate-Only Comparison:
 *   Actual disbursements PLUS every actual historical RateChangeEvent, at
 *   its real effective date and real rate — but with NO prepayments, NO
 *   balance corrections, and each rate change's EMI consequence
 *   RECONSTRUCTED (never copied from the real event) to auto-recalculate:
 *   at each rate change, EMI is solved fresh from (principal at that
 *   point under this SAME baseline's own trajectory, the new rate, and
 *   the ORIGINAL tenure minus cycles completed by that point) — never
 *   from a manually-chosen EMI/tenure the user actually picked in the
 *   real event's consequence. This isolates the effect of rate movements
 *   alone, independent of any deliberate repayment decision.
 *
 *   Constructing this baseline is inherently ITERATIVE: each successive
 *   rate change's synthetic EMI depends on the principal trajectory
 *   established by the previous synthetic rate changes, so this module
 *   builds the event list one rate change at a time, calling
 *   computeSchedule() after each addition to discover the principal at
 *   the next rate change's effective date before constructing that next
 *   synthetic event.
 *
 * BASELINE C — Current Trajectory:
 *   The complete, real, unrestricted trajectory — every actual event
 *   exactly as it happened/was recorded, projected to natural closure
 *   with no asOfDate cap. This is a direct, unmodified computeSchedule()
 *   call over the full active event list — identical to what a Current
 *   Projection summary section would show.
 */

export interface SavingsBaselines {
  baselineA: ComputedSchedule;
  baselineB: ComputedSchedule;
  baselineC: ComputedSchedule;
}

export interface SavingsSummary {
  interestSavedVsOriginal: number;
  totalPaymentSavedVsOriginal: number;
  /** Positive = the current trajectory closes SOONER than the original plan (in genuine, non-residual scheduled cycles). */
  timeSavedVsOriginalMonths: number;
  /**
   * The rate-only comparison, exposed separately so the effect of rate
   * movements is never conflated with the effect of deliberate
   * repayment actions (prepayments, chosen EMI/tenure overrides).
   * interestSavedVsOriginal - interestSavedDueToRateMovementAlone gives
   * the portion of savings attributable to the user's own actions.
   */
  interestSavedDueToRateMovementAlone: number;
  totalPaymentSavedDueToRateMovementAlone: number;
  timeSavedDueToRateMovementAloneMonths: number;
}

/**
 * Builds Baseline A: actual disbursements only, nothing else. The engine
 * naturally uses loan.originalAnnualRatePercent/originalEmiAtFullDisbursement
 * throughout when no rate-change/prepayment/correction events are present.
 */
export function computeBaselineA(loan: Loan, allActiveEvents: LoanEvent[]): ComputedSchedule {
  const disbursements = allActiveEvents.filter((e): e is DisbursementEvent => e.type === 'disbursement');
  return computeSchedule({ loan, events: disbursements });
}

/**
 * Builds Baseline B: actual disbursements + actual historical rate
 * changes (real dates/rates), each with a RECONSTRUCTED, auto-calculated
 * EMI consequence — never prepayments, never the real consequence's
 * actual stored newEmi/newRemainingTenureMonths.
 */
export function computeBaselineB(loan: Loan, allActiveEvents: LoanEvent[]): ComputedSchedule {
  const disbursements = allActiveEvents.filter((e): e is DisbursementEvent => e.type === 'disbursement');
  const realRateChanges = allActiveEvents
    .filter((e): e is RateChangeEvent => e.type === 'rate_change')
    .sort((a, b) => (a.effectiveDate < b.effectiveDate ? -1 : a.effectiveDate > b.effectiveDate ? 1 : 0));

  if (realRateChanges.length === 0) {
    // No historical rate changes at all — Baseline B is identical to
    // Baseline A in this case (nothing to isolate).
    return computeSchedule({ loan, events: disbursements });
  }

  // Build the synthetic event list ONE rate change at a time, since each
  // synthetic EMI depends on the principal trajectory established by the
  // previously-added synthetic rate changes.
  const syntheticEvents: LoanEvent[] = [...disbursements];

  for (const realRateChange of realRateChanges) {
    const effectiveDate = realRateChange.effectiveDate;

    // Principal immediately before this rate change, under BASELINE B's
    // OWN trajectory so far (i.e. with only the synthetic rate changes
    // added up to this point, never the real event history).
    const scheduleSoFar = computeSchedule({ loan, events: syntheticEvents, asOfDate: effectiveDate });
    const principalAtThisPoint = scheduleSoFar.summary.currentOutstandingPrincipal;
    const cyclesCompletedByThisPoint = countGenuineCycles(scheduleSoFar);
    // Remaining ORIGINAL contractual instalments: counted to contractual maturity so this baseline shares the
    // same horizon as the engine's assumed-EMI scenario (identical to originalTenure - cyclesCompleted when
    // regular EMI runs from the first due date; correct when a pre-EMI phase precedes it).
    const remainingOriginalTenure =
      remainingInstalmentsToMaturity(loan, allActiveEvents, effectiveDate) ??
      Math.max(1, loan.originalTenureMonths - cyclesCompletedByThisPoint);

    const recalculatedEmi = calculateEmi(principalAtThisPoint, realRateChange.annualRatePercent, remainingOriginalTenure);

    const consequence: RepaymentConsequence = {
      newEmi: recalculatedEmi,
      newRemainingTenureMonths: remainingOriginalTenure,
      primaryLever: 'tenure',
      status: 'projected',
      repaymentTermsEffectiveDate: effectiveDate,
    };

    syntheticEvents.push({
      id: `baseline-b-rate-change-${effectiveDate}`,
      loanId: loan.id,
      type: 'rate_change',
      effectiveDate,
      annualRatePercent: realRateChange.annualRatePercent,
      repaymentConsequence: consequence,
      createdAt: realRateChange.createdAt,
      updatedAt: realRateChange.createdAt,
    } satisfies RateChangeEvent);
  }

  return computeSchedule({ loan, events: syntheticEvents });
}

/**
 * Builds Baseline C: the complete, real, unrestricted trajectory —
 * every actual active event, projected to natural closure. Identical to
 * a "Current Projection" unrestricted computeSchedule() call.
 */
export function computeBaselineC(loan: Loan, allActiveEvents: LoanEvent[]): ComputedSchedule {
  return computeSchedule({ loan, events: allActiveEvents });
}

/** Computes all three baselines together (a small convenience wrapper — each baseline function remains independently usable/testable). */
export function computeSavingsBaselines(loan: Loan, allActiveEvents: LoanEvent[]): SavingsBaselines {
  return {
    baselineA: computeBaselineA(loan, allActiveEvents),
    baselineB: computeBaselineB(loan, allActiveEvents),
    baselineC: computeBaselineC(loan, allActiveEvents),
  };
}

/**
 * Derives the savings figures from three already-computed baselines.
 * Pure arithmetic differences only — no calculation logic here beyond
 * subtraction, per the explicit requirement that a prepayment's face
 * value must never be presented as "savings" on its own.
 */
export function computeSavingsSummary(baselines: SavingsBaselines): SavingsSummary {
  const { baselineA, baselineB, baselineC } = baselines;

  const totalPaymentA = baselineA.summary.totalEmiPaid + baselineA.summary.totalPrepayments;
  const totalPaymentB = baselineB.summary.totalEmiPaid + baselineB.summary.totalPrepayments;
  const totalPaymentC = baselineC.summary.totalEmiPaid + baselineC.summary.totalPrepayments;

  const cyclesA = countGenuineCycles(baselineA);
  const cyclesB = countGenuineCycles(baselineB);
  const cyclesC = countGenuineCycles(baselineC);

  return {
    interestSavedVsOriginal: baselineA.summary.totalInterestPaid - baselineC.summary.totalInterestPaid,
    totalPaymentSavedVsOriginal: totalPaymentA - totalPaymentC,
    timeSavedVsOriginalMonths: cyclesA - cyclesC,

    interestSavedDueToRateMovementAlone: baselineA.summary.totalInterestPaid - baselineB.summary.totalInterestPaid,
    totalPaymentSavedDueToRateMovementAlone: totalPaymentA - totalPaymentB,
    timeSavedDueToRateMovementAloneMonths: cyclesA - cyclesB,
  };
}
